import InfoRounded from '@mui/icons-material/InfoRounded';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import { AlertTooltip } from '@openx/components-core/lib/AlertTooltip/AlertTooltip.tsx';
import { Button } from '@openx/components-core/lib/Button/Button.tsx';
import { AppInventoryVersion, type AppInventoryVersionType } from '@openx/types/targeting/appInventory.ts';
import { AppInventoryType, InventoryContentOption } from '@openx/types/targeting/inventoryAndContent.ts';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { InventoryContentOptionDropdown } from '../../../shared/InventoryAndContent/components/InventoryContentOptionDropdown.tsx';
import type { TopBoxRendererProps } from '../../../shared/InventoryAndContent/components/TopBoxRenderer.tsx';
import { useAppInventoryAllowed } from '../../../shared/InventoryAndContent/featureFlags.ts';
import { DrawerTopBox } from '../../../shared/TargetingDrawer/DrawerTopBox.tsx';

type InventoryTopBoxRendererProps = Pick<
  TopBoxRendererProps,
  | 'contentOption'
  | 'parentAccountFeatures'
  | 'setContentOption'
  | 'showBulkAdd'
  | 'isLimitReached'
  | 'onBulkOpen'
  | 'appInventoryType'
> & {
  drawerContent: Record<string, React.ReactNode>;
  inventoryVersion?: AppInventoryVersionType;
};

export const InventoryTopBoxRenderer = ({
  contentOption,
  parentAccountFeatures,
  setContentOption,
  drawerContent,
  showBulkAdd,
  isLimitReached,
  onBulkOpen,
  appInventoryType,
  inventoryVersion,
}: InventoryTopBoxRendererProps) => {
  const { t } = useTranslation();
  const isAppInventoryAllowed = useAppInventoryAllowed();

  // Dynamic Bulk Add button label for App Inventory
  const bulkAddLabel = useMemo(() => {
    if (contentOption === InventoryContentOption.APP_GROUP && appInventoryType) {
      const typeName = appInventoryType === AppInventoryType.APP_NAME ? t('App Name') : t('App Bundle ID');
      return `${t('Bulk Add')} - ${typeName}`;
    }
    return t('Bulk Add');
  }, [contentOption, appInventoryType, t]);

  const isAppGroup = contentOption === InventoryContentOption.APP_GROUP;

  // Disable Bulk Add when Inventory List type is selected (no manual input to bulk-add)
  const isInventoryListSelected = isAppGroup && appInventoryType === AppInventoryType.INVENTORY_LIST;

  return (
    <DrawerTopBox>
      <Grid
        container
        spacing={1}
        sx={{
          alignItems: 'flex-start',
          flexGrow: 1,
        }}
      >
        {inventoryVersion === AppInventoryVersion.APP_INVENTORY_ONLY_V2 && isAppInventoryAllowed ? null : (
          <Grid size={isAppGroup ? 5 : 3}>
            <InventoryContentOptionDropdown
              value={contentOption}
              onChange={setContentOption}
              parentAccountFeatures={parentAccountFeatures}
              inventoryVersion={inventoryVersion}
            />
          </Grid>
        )}

        {isAppGroup ? drawerContent[contentOption] : <Grid size={9}>{drawerContent[contentOption]}</Grid>}

        {showBulkAdd && (
          <Grid size={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Button disabled={isLimitReached || isInventoryListSelected} onClick={onBulkOpen}>
                {bulkAddLabel}
              </Button>
              {isInventoryListSelected && (
                <AlertTooltip
                  icon={<InfoRounded />}
                  color="default"
                  title={t('Bulk Add is not available for App Bundle ID - From Inventory List.')}
                />
              )}
            </Box>
          </Grid>
        )}
      </Grid>
    </DrawerTopBox>
  );
};
